import * as React from 'react'
import classNames from 'classnames'
import { DisabledItemHoverInfotip } from 'uiComponents/infotip'
import './segmentedControl.scss'

export interface SegmentOption<T extends string> {
    value: T
    label: string
    disabled?: boolean
    disabledReason?: string
}

export type SegmentedControlSize = 'page' | 'compact'

interface SegmentedControlProps<T extends string> {
    options: SegmentOption<T>[]
    value: T
    onChange: (value: T) => void
    ariaLabel: string
    size?: SegmentedControlSize
    fit?: boolean
    className?: string
}

export function SegmentedControl<T extends string>({
    options,
    value,
    onChange,
    ariaLabel,
    size = 'page',
    fit,
    className,
}: SegmentedControlProps<T>) {
    const buttons = React.useRef<Record<string, HTMLButtonElement | null>>({})
    const activeIndex = options.findIndex((option) => option.value === value)
    const selectable = options.filter((option) => !option.disabled)
    const tabStop = selectable.find((option) => option.value === value)?.value ?? selectable[0]?.value

    const pick = (option: SegmentOption<T>) => {
        onChange(option.value)
        buttons.current[option.value]?.focus()
    }

    const step = (direction: 1 | -1) => {
        if (selectable.length === 0) {
            return
        }
        const current = selectable.findIndex((option) => option.value === value)
        const from = current === -1 ? (direction === 1 ? -1 : 0) : current
        pick(selectable[(from + direction + selectable.length) % selectable.length])
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
            event.preventDefault()
            step(1)
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
            event.preventDefault()
            step(-1)
        } else if (event.key === 'Home' && selectable.length > 0) {
            event.preventDefault()
            pick(selectable[0])
        } else if (event.key === 'End' && selectable.length > 0) {
            event.preventDefault()
            pick(selectable[selectable.length - 1])
        }
    }

    const style = {
        '--segment-count': options.length,
        '--segment-index': Math.max(0, activeIndex),
    } as React.CSSProperties

    const segment = (option: SegmentOption<T>) => (
        <button
            type="button"
            role="radio"
            aria-checked={option.value === value}
            tabIndex={option.value === tabStop ? 0 : -1}
            disabled={option.disabled}
            className={classNames('segmented-option', { active: option.value === value })}
            onClick={() => onChange(option.value)}
            ref={(el) => {
                buttons.current[option.value] = el
            }}
        >
            {option.label}
        </button>
    )

    return (
        <div
            className={classNames('segmented', `segmented-${size}`, { 'segmented-fit': fit }, className)}
            style={style}
        >
            <div className="segmented-options" role="radiogroup" aria-label={ariaLabel} onKeyDown={onKeyDown}>
                {activeIndex > -1 && !fit && <span className="segmented-indicator" aria-hidden="true" />}
                {options.map((option) =>
                    option.disabled && option.disabledReason ? (
                        <DisabledItemHoverInfotip
                            key={option.value}
                            active
                            width="18em"
                            infotipText={option.disabledReason}
                        >
                            {segment(option)}
                        </DisabledItemHoverInfotip>
                    ) : (
                        <React.Fragment key={option.value}>{segment(option)}</React.Fragment>
                    ),
                )}
            </div>
        </div>
    )
}
